import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section } from "@/components/marketing/sections/Section"

const DESCRIPTION =
  "How FullCast handles affiliate links: what they are, when we use them, and our editorial standard for which tools we recommend."

export default function Disclosure() {
  return (
    <MarketingLayout>
      <SeoHead title="Affiliate Disclosure" description={DESCRIPTION} />

      <Section containerClassName="max-w-3xl">
        <h1 className="display-2">Affiliate Disclosure</h1>
        <p className="mt-3 text-sm text-ink-muted">Last updated: 2026-06-25</p>

        <div className="body-content mt-8">
          <p>
            Some of the tools we link to from this site (and from emails we send)
            are <strong>affiliate links</strong>. If you click through and sign up
            for the underlying product, FullCast may receive a small commission at
            no additional cost to you.
          </p>

          <h2>Our editorial standard</h2>
          <p>
            We only recommend tools we actually use to produce client shows. The
            commissions don't change the recommendations — we'd recommend the
            same tools whether or not we earned anything for it. If a tool we
            link to ever stops earning its place editorially, we'd drop the
            recommendation regardless of what it pays.
          </p>
          <p>
            The diagnostic question we apply to every affiliate link:{" "}
            <em>would I recommend this if I made no money from it?</em> If the
            answer is yes, we link with confidence. If no, we don't link at all.
          </p>

          <h2>Where you'll see affiliate links</h2>
          <p>
            Affiliate links currently appear in a few specific places:
          </p>
          <ul>
            <li>
              <Link href="/stack" className="text-accent-display">The FullCast Stack</Link>
              {" "}— our canonical list of tools we use for client shows.
            </li>
            <li>
              Specific blog posts where a tool is genuinely the right
              recommendation — primarily our hosting recommendation in{" "}
              <Link href="/blog/how-to-start-a-podcast" className="text-accent-display">
                How to start a podcast
              </Link>
              ,{" "}
              <Link href="/blog/podcast-production-cost" className="text-accent-display">
                What it really costs to run a podcast
              </Link>
              , and{" "}
              <Link href="/blog/the-list-of-tools-and-resources-we-use-at-fullcast" className="text-accent-display">
                The list of tools and resources we use at FullCast
              </Link>
              .
            </li>
            <li>
              The signature of email broadcasts sent from{" "}
              <strong>harry@fullcast.co</strong> via our newsletter.
            </li>
          </ul>

          <h2>Specific affiliate programs</h2>
          <p>
            The affiliate programs we currently participate in:
          </p>
          <ul>
            <li>
              <strong>Captivate.fm</strong> — our default hosting recommendation;
              the host we use for every FullCast client show. Affiliate link:{" "}
              <a href="/captivate" className="text-accent-display">fullcast.co/captivate</a>.
            </li>
          </ul>
          <p>
            We'll update this page as we add or remove affiliate relationships.
            If a link on this site goes to a vendor not listed here, it isn't an
            affiliate link.
          </p>

          <h2>Why we tell you</h2>
          <p>
            US Federal Trade Commission guidelines require that material
            connections between endorsers and advertisers be disclosed when those
            connections might affect how readers interpret a recommendation. We
            agree with the principle and would disclose even if it weren't
            required.
          </p>
          <p>
            If you spot a recommendation on this site that you think we haven't
            disclosed properly, email{" "}
            <a href="mailto:hello@fullcast.co" className="text-accent-display">
              hello@fullcast.co
            </a>{" "}
            and we'll fix it.
          </p>
        </div>
      </Section>
    </MarketingLayout>
  )
}
